import { gql, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { deepCloneAndOmit } from 'src/utils/objects';
import { BehaviorConfig } from './behavior-config.types';

const GET_BEHAVIOR_CONFIG_QUERY = gql`
  query GetBehaviorConfig {
    getBehaviorConfig {
      version
      triggers {
        label
        value
      }
      conditionTypes {
        label
        value
      }
      actionTypes {
        label
        value
      }
      operators {
        text {
          value
          label
          arity
          triggers
        }
        textarea {
          value
          label
          arity
          triggers
        }
        list {
          value
          label
          arity
          triggers
        }
        number {
          value
          label
          arity
          triggers
        }
        date {
          value
          label
          arity
          triggers
        }
        boolean {
          value
          label
          arity
          triggers
        }
      }
      conditions {
        partnerBusinessHours {
          triggers
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          group
        }
        actor {
          triggers
          attributeLabel
          uiType
          attribute
          operators {
            value
            label
            arity
          }
          options {
            value
            label
            __typename
          }
          group
          __typename
        }
        partnerSetting {
          triggers
          attributeLabel
          uiType
          attribute
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          group
        }
        data {
          triggers
          group
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          optionsQuery {
            graphqlQuery
          }
        }
        contactCenterHours {
          triggers
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          group
        }
        customSchedule {
          triggers
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          group
        }
      }
      actions {
        data {
          triggers
          group
          attribute
          attributeLabel
          uiType
          options {
            value
            label
          }
          optionsQuery {
            graphqlQuery
          }
        }
        sendSlackMessage {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            options {
              value
              label
            }
          }
        }
        sendConversationMessage {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            dependsOn {
              attribute
              value
            }
            options {
              value
              label
            }
          }
        }
        manageConversationLabels {
          triggers
          attributes {
            name
            uiType
            label
            multiple
            optionsQuery {
              graphqlQuery
            }
            options {
              value
              label
            }
          }
        }
        manageSubAssignees {
          triggers
          attributes {
            name
            uiType
            label
            multiple
            optionsQuery {
              graphqlQuery
            }
            options {
              value
              label
            }
          }
        }
        webhook {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            placeholderByTrigger {
              trigger
              placeholder
            }
            options {
              value
              label
            }
          }
        }
        signal {
          triggers
          attributes {
            name
            uiType
            label
            dependsOn {
              attribute
              value
            }
            options {
              value
              label
            }
          }
        }
        createTwilioTask {
          triggers
          attributes {
            name
            uiType
            label
            options {
              label
              value
            }
          }
        }
        runTriage {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            options {
              label
              value
            }
          }
        }
        deleteConversation {
          triggers
          attributes {
            name
            uiType
            label
          }
        }
        blockConversationRequester {
          triggers
          attributes {
            name
            uiType
            label
          }
        }
        createConversationMessageDraft {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            dependsOn {
              attribute
              value
            }
            options {
              value
              label
            }
          }
        }
      }
    }
  }
`;

interface GetBehaviorConfigQuery {
  getBehaviorConfig: BehaviorConfig & { __typename: string };
}

export const useBehaviorConfig = () => {
  const { user } = useCurrentUserProfile();
  const { data: behaviorConfigData, loading: behaviorConfigLoading } =
    useQuery<GetBehaviorConfigQuery>(GET_BEHAVIOR_CONFIG_QUERY, {
      fetchPolicy: 'cache-and-network',
      context: {
        includePlatformLevelData: user?.partnerId === PLATFORM_PARTNER_ID,
        // The 12h TTL link is only safe for queries the WebSocket eviction handler
        // can invalidate, which needs an `@entity` directive this query does not
        // carry. Its payload aggregates partner-mutable data (triage
        // configurations, labels, custom attributes), so without ttl: 0 an edit
        // made elsewhere in Settings stays invisible here for up to 12 hours.
        ttl: 0,
      },
    });

  const configWithoutTypenameKeys = useMemo(
    () =>
      behaviorConfigData?.getBehaviorConfig
        ? deepCloneAndOmit<BehaviorConfig & { __typename: string }>(
            behaviorConfigData?.getBehaviorConfig,
            ['__typename'],
          )
        : null,
    [behaviorConfigData?.getBehaviorConfig],
  );

  return {
    behaviorConfig: configWithoutTypenameKeys,
    loading: behaviorConfigLoading,
  };
};
